import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface SEOConfig {
  title?: string;
  description?: string;
  image?: string;
  type?: "website" | "article" | "profile";
  noindex?: boolean;
}

const BASE_URL = "https://firststage.ca";
const DEFAULT_IMAGE = `${BASE_URL}/og-image.jpg`;

const useSEO = (config: SEOConfig = {}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.substring(0, 2) || "en";

  useEffect(() => {
    const {
      title,
      description,
      image = DEFAULT_IMAGE,
      type = "website",
      noindex = false,
    } = config;

    const siteName = currentLang === "fr" 
      ? "First Stage Talent Agency | Le PREMIER CHOIX pour vos projets de voix!" 
      : "First Stage Talent Agency | The FIRST CHOICE for your voiceover projects!";
    
    const defaultDescription = currentLang === "fr"
      ? "Votre PREMIER CHOIX pour les talents de voix hors pair! First Stage connecte les producteurs avec des artistes vocaux bilingues professionnels à travers le Canada."
      : "Your FIRST CHOICE for outstanding voiceover talent! First Stage connects producers with professional bilingual voice artists across Canada.";

    const pageTitle = title ? `${title} | First Stage Talent` : siteName;
    const pageDescription = description || defaultDescription;

    // Update document title
    document.title = pageTitle;

    // Helper to update or create meta tags
    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Basic meta tags
    setMeta("description", pageDescription);
    setMeta("robots", noindex ? "noindex, nofollow" : "index, follow");

    // Open Graph
    setMeta("og:title", pageTitle, true);
    setMeta("og:description", pageDescription, true);
    setMeta("og:image", image, true);
    setMeta("og:type", type, true);
    setMeta("og:site_name", "First Stage Talent", true);
    setMeta("og:locale", currentLang === "fr" ? "fr_CA" : "en_CA", true);

    // Twitter Card
    setMeta("twitter:title", pageTitle);
    setMeta("twitter:description", pageDescription);
    setMeta("twitter:image", image);
    setMeta("twitter:card", "summary_large_image");

  }, [config, currentLang]);
};

export default useSEO;
